import React, { useContext, useEffect, useMemo } from "react";
import { Box } from "@material-ui/core";
import { RouteComponentProps } from "react-router";
import {
  useDeviceTypes,
  CarConfirmation,
  BOOKING_SUCCESS_TITLE,
  OfferAppliedBanner,
} from "halifax";
import clsx from "clsx";
import { CallState, CorpSessionInfo, ModalNames } from "redmond";
import { isCorpTenant, useIsTravelApp, notifyWalletDataChanged } from "@capone/common";
import { useExperimentsById } from "@capone/experiments";
import { useMedalliaBookingData } from "b2b-base/src/components/MedalliaFeedback";
import { ExpenseManagementEnrollModal } from "@capone-corporate/portal";
import { updateUserSeenModal } from "b2b-base/src/api/v1/user/updateUserSeenModal";
import { PATH_HOME, PATH_TRAVEL_SALE } from "../../../../utils/paths";
import * as textConstants from "./textConstants";
import "./styles.scss";
import {
  AVAILABLE,
  EXPENSE_MANAGEMENT_MODAL_EXP,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  TRAVEL_SALE_VARIANTS,
  TRAVEL_SALE,
  TREES_MODAL_EXPERIMENT,
  useExperiments,
  TRAVEL_SALE_ACTIVE,
  TRAVEL_APP_GTM_EXPERIMENT,
} from "../../../../context/experiments";
import { config } from "../../../../api/config";
import { ClientContext } from "../../../../App";
import { BookingSuccessModalConnectorProps } from "./container";
import { trackEvent } from "../../../../api/v1/analytics/trackEvent";

export interface IBookingSuccessModalProps
  extends BookingSuccessModalConnectorProps,
    RouteComponentProps {}

export const BookingSuccessModal = (props: IBookingSuccessModalProps) => {
  const {
    history,
    selectedVehicle,
    confirmation,
    confirmationDetailsCallState,
    resetBookState,
    earnString,
    email,
    userPassengers,
    selectedPassengerId,
    offerToApply,
    loyaltyNumber,
  } = props;
  const { matchesMobile } = useDeviceTypes();
  const isCorporate = isCorpTenant(config.TENANT);
  const { isTravelApp } = useIsTravelApp();
  const expState = useExperiments();
  const isEmEnrollModalEnabled =
    useExperimentsById(EXPENSE_MANAGEMENT_MODAL_EXP)?.variant === AVAILABLE;
  const treesModalExperiment = getExperimentVariant(
    expState.experiments,
    TREES_MODAL_EXPERIMENT,
  );
  const isTreesModalExperiment = useMemo(
    () => treesModalExperiment === AVAILABLE,
    [treesModalExperiment],
  );

  const travelSalesEventVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    TRAVEL_SALE,
    TRAVEL_SALE_VARIANTS,
  );

  const travelAppGTMExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_APP_GTM_EXPERIMENT,
  );
  const isTravelAppGTMExperiment = useMemo(
    () => travelAppGTMExperiment === AVAILABLE,
    [travelAppGTMExperiment],
  );

  const { sessionInfo } = useContext(ClientContext);

  const userRoles =
    sessionInfo && "corporateInfo" in sessionInfo
      ? sessionInfo.corporateInfo.role
      : [];
  const isAdmin = userRoles.includes("Admin");

  const hasSeenNfuInfoModal = (sessionInfo as CorpSessionInfo).corporateInfo
    ?.hasSeenModalMap?.[ModalNames.NFU_IN_PRODUCT_AWARENESS_CONFIRMATION];

  const selectedDriver = useMemo(
    () => userPassengers.find((user) => user.id === selectedPassengerId),
    [userPassengers, selectedPassengerId],
  );

  const isSelectedTravelerGhost =
    selectedDriver &&
    "isGhostUser" in selectedDriver &&
    selectedDriver.isGhostUser;

  const shouldShowNfuProductAwareness = Boolean(
    isAdmin && !hasSeenNfuInfoModal && isSelectedTravelerGhost,
  );

  const getUserName = () => selectedDriver
      ? `${selectedDriver?.givenName} ${selectedDriver?.surname}`
      : "your users";

  const bookingConfirmationId =
    confirmation?.confirmationInfo?.supplierBookingId ||
    confirmation?.confirmationInfo?.providerBookingId ||
    confirmation?.groundBookingId;

  // TODO: since 'vehicle' is not preserved on refresh, it would cause undefined error;
  // discuss with PM regarding what the expected behaviour is (e.g.: handle it properly with error modals, or preserve this state).
  if (!selectedVehicle || !confirmation) {
    history.push(PATH_HOME);
    return null;
  }

  const treesModalText = {
    treesModalHeader: textConstants.TREES_MODAL_HEADER,
    treesModalTitle: textConstants.TREES_MODAL_TITLE,
    treesModalSubtitle: textConstants.TREES_MODAL_SUBTITLE,
    treesModalImgLocation: textConstants.TREES_MODAL_IMG_LOCATION,
    treesModalLinkCopy: textConstants.TREES_MODAL_CTA_TEXT,
    treesModalBoldedLinkCopy: textConstants.TREES_BOLDED_MODAL_CTA_TEXT,
  };

  useEffect(() => {
    if (
      confirmationDetailsCallState === CallState.Success &&
      bookingConfirmationId
    ) {
      notifyWalletDataChanged("cars confirmation", bookingConfirmationId);
    }
  }, [bookingConfirmationId, confirmationDetailsCallState]);

  useMedalliaBookingData(bookingConfirmationId ?? undefined);

  useEffect(() => {
    const unlisten = history.listen((_, action) => {
      if (action === "PUSH") {
        resetBookState();
      } else if (action === "POP") {
        history.push(PATH_HOME);
      }
    });

    return () => unlisten();
  }, [history]);

  return (
    <>
      {isCorporate && offerToApply && (
        <OfferAppliedBanner
          offerToApply={offerToApply}
          isMobile={matchesMobile}
          onViewWalletClick={() => history.push("/travel-offers")}
        />
      )}
      <Box
        className={clsx("car-booking-success-container", {
          mobile: matchesMobile,
        })}
      >
        <CarConfirmation
          bannerCopy={textConstants.BANNER_COPY}
          title={BOOKING_SUCCESS_TITLE(
            true,
            selectedDriver ? [selectedDriver] : [],
            email || "",
          )}
          subtitle={textConstants.SUBTITLE(bookingConfirmationId ?? "")}
          loyaltyNumber={loyaltyNumber}
          nextHeader={textConstants.WHATS_NEXT_HEADER}
          infoCardTitles={{
            flightsTitle: textConstants.FLIGHTS_TITLE,
            flightsDescription: textConstants.FLIGHTS_DESCRIPTION,
            hotelsTitle: textConstants.HOTELS_TITLE,
            hotelsDescription: textConstants.HOTELS_DESCRIPTION,
          }}
          onFlightsClick={() => {
            history.push("/flights/");
            resetBookState();
          }}
          onHotelsClick={() => {
            history.push("/hotels/");
            resetBookState();
          }}
          isMobile={matchesMobile}
          vehicle={selectedVehicle}
          pickUp={confirmation.pickUp}
          dropOff={confirmation.dropOff}
          earnString={earnString}
          hidePriceAndReward={matchesMobile && true}
          displayTrees={isTreesModalExperiment}
          treesModalText={treesModalText}
          travelSalesBannerProps={
            travelSalesEventVariant === TRAVEL_SALE_ACTIVE
              ? {
                  subtitle: textConstants.TRAVEL_SALES_EVENT_ACTIVE_SUBTITLE,
                  ctaText: textConstants.TRAVEL_SALES_EVENT_ACTIVE_CTA,
                  ctaOnClick: () => {
                    const path = `${PATH_TRAVEL_SALE}?entryType=cars_confirmation`;
                    if (matchesMobile) {
                      history.push(path);
                    } else {
                      window.open(path, "_blank");
                    }
                  },
                }
              : undefined
          }
          isProdEnv={window.__mclean_env__.ENV === "production"}
          tenant={config.TENANT}
          shouldShowNfuProductAwareness={shouldShowNfuProductAwareness}
          travelerName={getUserName()}
          handleSeenNfuInfoModal={updateUserSeenModal}
          trackEvent={trackEvent}
          showTravelAppBanner={isTravelAppGTMExperiment && !isTravelApp}
        />
        {isCorporate && isEmEnrollModalEnabled && <ExpenseManagementEnrollModal />}
      </Box>
    </>
  );
};
